import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ValidationFlowBuilder } from './ValidationFlowBuilder';
import { apiService } from '../services/api';

interface ValidationFlow {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  flow_data: {
    nodes: any[];
    connections: any[];
  };
}

export const ValidationConfiguration: React.FC = () => {
  const [validationFlows, setValidationFlows] = useState<ValidationFlow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'list' | 'builder'>('list');
  const [editingFlow, setEditingFlow] = useState<ValidationFlow | null>(null);

  useEffect(() => {
    loadValidationFlows();
  }, []);

  const loadValidationFlows = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getValidationRules();
      if (response.data) {
        // Transform the API response to match our ValidationFlow interface
        const flows: ValidationFlow[] = response.data.validation_rules.map((rule: any) => ({
          id: rule.id,
          name: rule.name,
          description: rule.description,
          enabled: rule.enabled,
          created_at: rule.created_at || new Date().toISOString(),
          updated_at: rule.updated_at || new Date().toISOString(),
          flow_data: {
            nodes: rule.operations || [],
            connections: []
          }
        }));
        setValidationFlows(flows);
      } else {
        // Default flows for demonstration
        setValidationFlows([
          {
            id: 'supplier_validation',
            name: 'Supplier Validation Flow',
            description: 'Validates supplier information against VMD database',
            enabled: true,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z',
            flow_data: { nodes: [], connections: [] }
          },
          {
            id: 'po_validation', 
            name: 'PO Validation Flow',
            description: 'Validates purchase order information against PO Header database',
            enabled: true,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z',
            flow_data: { nodes: [], connections: [] }
          },
          {
            id: 'amount_validation',
            name: 'Amount Validation Flow',
            description: 'Validates invoice amounts using numeric threshold comparison',
            enabled: false,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z',
            flow_data: { nodes: [], connections: [] }
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading validation flows:', error);
      setValidationFlows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewFlow = () => {
    const newFlow: ValidationFlow = {
      id: `flow_${Date.now()}`,
      name: 'New Validation Flow',
      description: 'Custom validation flow',
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      flow_data: { nodes: [], connections: [] }
    };
    setEditingFlow(newFlow);
    setCurrentView('builder');
  };

  const handleEditFlow = (flow: ValidationFlow) => {
    setEditingFlow(flow);
    setCurrentView('builder');
  };

  const handleToggleFlow = async (flowId: string, enabled: boolean) => {
    try {
      const response = await apiService.toggleValidationRule(flowId, enabled);
      if (response.error) {
        alert('Error toggling flow: ' + response.error);
      } else {
        setValidationFlows(prev => prev.map(flow => 
          flow.id === flowId ? { ...flow, enabled } : flow
        ));
      }
    } catch (error) {
      console.error('Error toggling validation flow:', error);
    }
  };

  const handleDeleteFlow = async (flowId: string) => {
     
    if (confirm('Are you sure you want to delete this validation flow?')) {
      try {
        const response = await apiService.deleteValidationRule(flowId);
        if (response.error) {
          alert('Error deleting flow: ' + response.error);
        } else {
          setValidationFlows(prev => prev.filter(flow => flow.id !== flowId));
        }
      } catch (error) {
        console.error('Error deleting validation flow:', error);
      }
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setEditingFlow(null);
  };

  const renderFlowList = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Validation Configuration
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create and manage custom validation flows using atomic operations
          </p>
        </div>
        <Button onClick={handleCreateNewFlow} className="bg-blue-600 hover:bg-blue-700">
          + Create Flow
        </Button>
      </div>

      {/* Quick Start Guide */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🧩</div>
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Build Custom Validations
              </h3>
              <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
                Use the visual flow builder to combine atomic operations like normalize_number, 
                fuzzy_match, and get_field_value to create powerful validation logic.
              </p>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                  Drag & Drop
                </span>
                <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                  Visual Connections
                </span>
                <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                  Real-time Testing
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flows Table */}
      <Card>
        <CardHeader>
          <CardTitle>Validation Flows ({validationFlows.length} flows)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading validation flows...</div>
            </div>
          ) : validationFlows.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No validation flows yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first custom validation flow using our visual builder
              </p>
              <Button onClick={handleCreateNewFlow}>
                Create Your First Flow
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Name
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Description
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Operations
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Status
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Last Updated
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {validationFlows.map((flow) => (
                    <tr key={flow.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800">
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {flow.name}
                        </div>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <div className="text-gray-700 dark:text-gray-300 text-sm">
                          {flow.description}
                        </div>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {flow.flow_data.nodes.length} nodes
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {flow.flow_data.connections.length} connections
                          </span>
                        </div>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={flow.enabled}
                            onChange={(e) => handleToggleFlow(flow.id, e.target.checked)}
                            className="rounded"
                          />
                          <span className={`text-sm ${flow.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                            {flow.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </label>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(flow.updated_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleEditFlow(flow)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Edit Flow
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFlow(flow.id)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderFlowBuilder = () => (
    <div className="h-full">
      <div className="flex items-center gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <Button onClick={handleBackToList} variant="outline">
          ← Back to Flows
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {editingFlow?.name || 'New Flow'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Build validation logic using atomic operations
          </p>
        </div>
      </div>
      <div className="h-[calc(100vh-200px)]">
        <ValidationFlowBuilder />
      </div>
    </div>
  );

  if (currentView === 'builder') {
    return renderFlowBuilder();
  }

  return (
    <div className="p-6 mx-auto">
      {renderFlowList()}
    </div>
  );
};